import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RotateCcw, AlertTriangle } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../ui/dialog';
import { Checkbox } from '../../ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 'source',     label: '1 · Plan source',             hint: 'Supprime le plan et TOUT ce qui en dérive' },
  { id: 'transform',  label: '1 · Orientation & recadrage', hint: 'Rotation, symétries, crop' },
  { id: 'structure',  label: '2-3 · Délimitation & Structure', hint: 'Contour, murs, ouvertures, escaliers, brisures' },
  { id: 'rooms',      label: '4 · Pièces',                  hint: 'Pièces détectées / dessinées' },
  { id: 'validation', label: '1 · Échelle & statut revu',  hint: 'Calibration métrique, statut validé' },
];

/**
 * ResetPlanDialog — V14.69 Phase 1.
 * Réinitialisation à la carte des étapes du process Édition Plan pour le
 * lot courant. « Tout réinitialiser » coche tout et envoie steps=['all'].
 */
const ResetPlanDialog = ({ open, onOpenChange, projectId, lotId, onDone }) => {
  const [checked, setChecked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const allChecked = checked.size === STEPS.length;

  const toggle = (id) => setChecked((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const doReset = async () => {
    if (checked.size === 0) return;
    setBusy(true);
    try {
      const steps = allChecked ? ['all'] : Array.from(checked);
      const url = `${API}/projects/${projectId}/plan-source/reset${lotId ? `?lot_id=${lotId}` : ''}`;
      const r = await axios.post(url, { steps }, { withCredentials: true });
      toast.success(`Réinitialisé : ${(r.data?.cleared || []).join(', ')}`);
      setChecked(new Set());
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la réinitialisation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reset-plan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="w-4 h-4 text-rose-400" />
            Réinitialiser le lot courant
          </DialogTitle>
          <DialogDescription>
            Cochez les étapes à effacer. Action irréversible.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <button
            type="button"
            onClick={() => setChecked(allChecked ? new Set() : new Set(STEPS.map((s) => s.id)))}
            className={`w-full text-left px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
              allChecked
                ? 'border-rose-400/50 bg-rose-500/10 text-rose-300'
                : 'border-white/10 hover:bg-white/5'
            }`}
            data-testid="reset-all-toggle"
          >
            <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5" />
            Tout réinitialiser
          </button>
          {STEPS.map((s) => (
            <label
              key={s.id}
              className="flex items-start gap-2.5 px-3 py-1.5 rounded-lg hover:bg-white/[0.04] cursor-pointer"
              data-testid={`reset-step-${s.id}`}
            >
              <Checkbox
                checked={checked.has(s.id)}
                onCheckedChange={() => toggle(s.id)}
                className="mt-0.5"
                data-testid={`reset-check-${s.id}`}
              />
              <span className="min-w-0">
                <span className="block text-sm">{s.label}</span>
                <span className="block text-[11px] text-muted-foreground">{s.hint}</span>
              </span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Annuler
          </Button>
          <Button
            onClick={doReset}
            disabled={busy || checked.size === 0}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="reset-confirm-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Réinitialiser (${checked.size})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ResetPlanDialog;
